import DataParameters from 'enums/DataParametersForUtm';
import { locales } from 'languages';

const isBrowser = typeof window === 'object';
const isDocumentDefined = typeof document === 'object';

const generateUuid = () => {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}
	return `bnk-${Math.random().toString(36).slice(2)}${Date.now()}`;
};

export const getMessagesByLang = (langTranslations: { [key: string]: string }, lang: string) => langTranslations[lang];

export const getKeyByValue = (object: { [key: string]: string }, value: string) => Object.keys(object).find(key => object[key] === value);

export const validateString = (name: string) => {
	const regex = /^[a-zA-Z\s]*$/;
	return regex.test(name);
};

export const validateLatinExtended = (value: string) => {
	const regex = /^[a-zA-ZąĄčČęĘėĖįĮšŠųŲūŪžŽ\s]*$/;
	return regex.test(value);
};

export const validateLatinExtendedAndNumbers = (value: string) => {
	const regex = /^[0-9a-zA-ZąĄčČęĘėĖįĮšŠųŲūŪžŽ\s]*$/;
	return regex.test(value);
};

export const validateEmail = (email: string) => {
	const rgx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
	return rgx.test(email);
};

export const validatePhone = (number: string) => {
	const regex = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/g;
	return regex.test(number);
};

export const validateOnlySpaces = (value: string) => {
	const trimmedValue = value.replace(/\s/g, '');
	return trimmedValue.length > 0;
};

export const getCookie = (cname: string) => {
	if (!isDocumentDefined) {
		return '';
	}
	const name = `${cname}=`;
	const decodedCookie = decodeURIComponent(document.cookie);
	const ca = decodedCookie.split(';');
	for (let i = 0; i < ca.length; i++) {
		let c = ca[i];
		while (c.charAt(0) === ' ') {
			c = c.substring(1);
		}
		if (c.indexOf(name) === 0) {
			return c.substring(name.length, c.length);
		}
	}
	return '';
};

export const disableGoogleAnalytics = (trackingId: string, isGADisabled: boolean, isShort?: boolean) => {
	if (!isShort) {
		return `ga-disable-${trackingId}=${isGADisabled}; expires=Thu, 31 Dec 2099 23:59:59 UTC;path=/`;
	}
	return `ga-disable-${trackingId}=${isGADisabled}`;
};

export const enableFacebookPixel = (enable: boolean) => `bankera-facebook-pixel=${enable}; expires=Thu, 31 Dec 2099 23:59:59 UTC;path=/`

const postUtm = (data: Record<string, unknown>) => {
	if (typeof fetch !== 'function') {
		return;
	}

	fetch(`/utm`, {
		method: 'post',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(data)
	})
		.then(response => response.json())
		.catch(() => undefined);
};

export const setRefTracking = (userEmail?: string) => {
	if (!isBrowser) {
		return;
	}

	const { localStorage } = window;
	const searchParameters = window.location.search || '';
	const parameters = new URLSearchParams(searchParameters);
	
	const referralParam = parameters.get(DataParameters.REF);
	const currentRef = getCookie(DataParameters.REF_ID) || localStorage.getItem(DataParameters.REF);
	if (referralParam) localStorage.setItem(DataParameters.REF, referralParam);
	
	const referralValue = referralParam || currentRef;

	const data: Record<string, unknown> = {
		referral: referralValue,
		referrer: localStorage.getItem(DataParameters.REFERRER),
		url: window.location.href
	};
	
	if (referralValue && userEmail) {
		data.user_email = userEmail;
		postUtm(data);
		return;
	}
	
	if (referralParam && !userEmail) {
		postUtm(data);
	}
};

export const setUTM = (userEmail?: string) => {
	if (!isBrowser) {
		return;
	}

	const { localStorage } = window;
	const searchParameters = window.location.search || '';
	const parameters = new URLSearchParams(searchParameters);
	const UUID = localStorage.getItem(DataParameters.UTM_UUID);
	const utmCookie = getCookie(DataParameters.UTM_UUID);
	
	const utmSource = parameters.get(DataParameters.UTM_SOURCE);

	if (utmSource && !userEmail) {
		const uuid = generateUuid();
		if (!UUID) localStorage.setItem(DataParameters.UTM_UUID, uuid);
		
		const data: Record<string, unknown> = {
			utm_uuid: localStorage.getItem(DataParameters.UTM_UUID),
			utm_source: utmSource,
			utm_medium: parameters.get(DataParameters.UTM_MEDIUM) ?? undefined,
			utm_campaign: parameters.get(DataParameters.UTM_CAMPAIGN) ?? undefined,
			utm_term: parameters.get(DataParameters.UTM_TERM) ?? undefined,
			utm_content: parameters.get(DataParameters.UTM_CONTENT) ?? undefined,
			utm_referrer: localStorage.getItem(DataParameters.REFERRER),
		};
		
		postUtm(data);
		return;
	}
		
	const referral = parameters.get(DataParameters.REF);

	if ((userEmail && UUID && !referral) || (userEmail && utmCookie && !referral)) {
		const data: Record<string, unknown> = {
			utm_uuid: utmCookie || UUID,
			user_email: userEmail
		};
		postUtm(data);
		return;
	}
		
	if (userEmail && referral) {
		setRefTracking(userEmail);
	}
};

export const clearObject = (obj: Record<string, unknown>) => {
	Object.keys(obj).forEach((key) => {
		if (key !== 'step') {
			// eslint-disable-next-line no-param-reassign
			delete obj[key];
		}
	});
};

export const getLanguageFromPath = (path: string) => {
    if (!path) {
        return locales[0];
    }
    const language = path.split('/')[1];
    return locales.includes(language) ? language : locales[0];
};
